import type { ComponentProps } from 'react'

import { tv } from 'tailwind-variants'

import {
  CheckIcon,
  CircleAlertIcon,
  Loader2Icon,
  PencilIcon,
  PencilOffIcon,
  Trash2Icon,
} from 'lucide-react'

import { CellToolbarItem } from './CellToolbar'

import { useCellDerivedState, useCellStateActions } from '../CellContext'
import { useGridSnap } from '../../grid/grid.store'

/**
 * Reuseable toolbar item for CellToolbar
 */
export function CellToolbarItemEdit() {
  const { isReadOnly, isEditing } = useCellDerivedState()
  const { setIsEditing } = useCellStateActions()

  if (isEditing) return null

  if (isReadOnly) {
    return (
      <CellToolbarItem tooltip="This cue is read-only." data-disabled={true}>
        <PencilOffIcon />
      </CellToolbarItem>
    )
  }

  return (
    <CellToolbarItem
      tooltip="Edit content"
      tooltipShortcut={['enter']}
      onClick={() => setIsEditing(true)}
    >
      <PencilIcon />
    </CellToolbarItem>
  )
}

/**
 * Reuseable toolbar item for CellToolbar
 */
export function CellToolbarItemDiscard({
  onClick,
}: Required<Pick<ComponentProps<'button'>, 'onClick'>>) {
  return (
    <CellToolbarItem
      tooltip="Discard changes"
      tooltipShortcut={['escape']}
      onClick={onClick}
    >
      <Trash2Icon />
    </CellToolbarItem>
  )
}

const saveButtonStyle = tv({
  base: '',
  variants: {
    status: {
      idle: 'hover:text-white',
      pending: 'text-gray-400 [&>svg]:animate-spin',
      success: 'text-green-500',
      error: 'text-red-500',
    },
  },
  defaultVariants: {
    status: 'idle',
  },
})

/**
 * Reuseable toolbar item for CellToolbar
 */
export function CellToolbarItemSave({
  onClick,
  ...props
}: ComponentProps<'button'>) {
  const { saveStatus } = useGridSnap().editor

  const Icon = (() => {
    switch (saveStatus.status) {
      case 'pending':
        return Loader2Icon
      case 'success':
        return CheckIcon
      case 'error':
        return CircleAlertIcon
      default:
        return CheckIcon
    }
  })()

  return (
    <CellToolbarItem
      {...props}
      tooltip={'Save changes'}
      tooltipShortcut={[['mod', 'enter']]}
      data-active={saveStatus.status === 'pending'}
      onClick={onClick}
      className={saveButtonStyle({ status: saveStatus.status })}
    >
      <Icon />
    </CellToolbarItem>
  )
}
